import { combine, createDomain, sample } from 'effector'
import { createGate, useStoreMap } from 'effector-react'
import { OtherCharge } from '~/shared/api/otherCharge'

import { createCache } from '~/shared/lib/mapCacheFactory'
import { isString } from '~/shared/lib/utils'

export const domain = createDomain('entities.otherCharge')

export const Gate = createGate<{ otherChargeId: UniqueId }>()

export const $otherChargeId = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { otherChargeId }) => otherChargeId)

export const requestOtherChargeFx = domain.createEffect<UniqueId, OtherCharge>({
  async handler(id) {
    return fetchOtherCharge(id)
  },
})

const fetchOtherCharge = async (id: UniqueId) => {
  const response = await OtherCharge.find(id)
  return response.getData() as OtherCharge
}

sample({
  clock: $otherChargeId,
  filter: isString,
  target: requestOtherChargeFx,
})

const {
  $cache: $otherChargesCache,
  useCache: useOtherChargesCache,
  updateCache,
} = createCache<OtherCharge>({
  domain,
  getEntityId: (otherCharge) => otherCharge.getApiId() as UniqueId,
})
export { $otherChargesCache, useOtherChargesCache }

export const $otherCharge = combine(
  $otherChargeId,
  $otherChargesCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id] ?? null
  },
)

$otherChargesCache.on(requestOtherChargeFx.doneData, (cache, otherCharge) =>
  updateCache(cache, [otherCharge]),
)

export const $otherChargeError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on(requestOtherChargeFx.fail, (store, { error, params: id }) => ({
    [id]: error,
    ...store,
  }))

export const useOtherChargeError = (id: UniqueId) =>
  useStoreMap($otherChargeError, (errors) => errors[id])
